import { auth } from '../../firebase';
import { getApiBaseUrl } from '../../config/apiConfig';

export type LogLevel = 'info' | 'warn' | 'error' | 'success';

export interface LogEntry {
  id: string;
  time: string;
  level: LogLevel;
  tag?: string;
  message: string;
  data?: any;
}

export interface DeviceLogItem {
  time: string;
  level: string;
  tag: string;
  message: string;
  data?: any;
}

export interface LoggerContext {
  tableId?: string;
  seat?: number | null;
  userId?: string;
  phase?: string;
}

type LogListener = (logs: LogEntry[]) => void;

function getDeviceId(): string {
  if (typeof window === 'undefined' || !window.localStorage) return 'server_or_unknown';
  const KEY = 'atl_device_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = `dev_${Math.random().toString(36).substring(2, 10)}_${Date.now().toString(36)}`;
    try { localStorage.setItem(KEY, id); } catch {}
  }
  return id;
}

function getPlatformString(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Linux/i.test(ua)) return 'Linux';
  return /Mobi/i.test(ua) ? 'Mobile' : 'Desktop';
}

function sanitizeLogData(data: any): any {
  if (data === undefined || data === null) return undefined;
  if (typeof data !== 'object') return data;
  if (data instanceof Error) return { message: data.message };
  try {
    const shallow: Record<string, any> = {};
    const keys = Object.keys(data).slice(0, 8);
    for (const k of keys) {
      const val = data[k];
      if (val === null || val === undefined) continue;
      if (typeof val === 'object') {
        shallow[k] = Array.isArray(val) ? `[Array(${val.length})]` : '{Object}';
      } else {
        shallow[k] = val;
      }
    }
    return shallow;
  } catch {
    return undefined;
  }
}

class ArenaLoggerService {
  private context: LoggerContext = {};
  private outgoingQueue: DeviceLogItem[] = [];
  private flushTimer: any = null;
  private isFlushing = false;
  private deviceId = getDeviceId();
  private platform = getPlatformString();
  private readonly MAX_QUEUE_SIZE = 250;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flushSync());
      window.addEventListener('atl:table-closed', () => {
        this.flushAsync().catch(() => {});
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flushSync();
        }
      });
    }
  }

  public setContext(ctx: Partial<LoggerContext>) {
    this.context = { ...this.context, ...ctx };
    if ('phase' in ctx && ctx.phase === undefined) {
      delete this.context.phase;
    }
    if ('tableId' in ctx && ctx.tableId === undefined) {
      delete this.context.tableId;
    }
    if ('seat' in ctx && (ctx.seat === undefined || ctx.seat === null)) {
      delete this.context.seat;
    }
  }

  public log(message: string, level: LogLevel = 'info', data?: any, tag: string = 'CLIENT') {
    const now = new Date();
    const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;

    // Output to devtools console
    const prefix = `[ARENA ${time}] [${tag}]`;
    if (level === 'error') {
      console.error(prefix, message, data || '');
    } else if (level === 'warn') {
      console.warn(prefix, message, data || '');
    } else {
      console.log(prefix, message, data || '');
    }

    // Queue sanitized telemetry item for cloud logging (no in-memory heap retention)
    this.enqueueCloudLog({
      time,
      level,
      tag,
      message,
      data: sanitizeLogData(data)
    });
  }

  public logAction(tag: string, message: string, data?: any, level: LogLevel = 'info') {
    this.log(message, level, data, tag);
  }

  public info(message: string, data?: any, tag: string = 'INFO') {
    this.log(message, 'info', data, tag);
  }

  public success(message: string, data?: any, tag: string = 'SUCCESS') {
    this.log(message, 'success', data, tag);
  }

  public warn(message: string, data?: any, tag: string = 'WARN') {
    this.log(message, 'warn', data, tag);
  }

  public error(message: string, data?: any, tag: string = 'ERROR') {
    this.log(message, 'error', data, tag);
  }

  public getLogs(): LogEntry[] {
    return [];
  }

  public clear() {
    // In-memory logs are eliminated
  }

  public subscribe(_listener: LogListener): () => void {
    return () => {};
  }

  private enqueueCloudLog(item: DeviceLogItem) {
    if (item.tag === 'RTDB_WIRE' || (item.message && item.message.includes('[RTDB_WIRE]'))) {
      return;
    }

    this.outgoingQueue.push(item);
    if (this.outgoingQueue.length > this.MAX_QUEUE_SIZE) {
      this.outgoingQueue.shift();
    }

    const isUrgent = item.level === 'error'
      || item.tag === 'BOT_FORCE_SUB'
      || item.tag === 'TURN_TIMEOUT'
      || item.tag === 'TIMER_EXPIRED';

    if (isUrgent) {
      if (this.flushTimer) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      this.flushAsync().catch(() => {});
      return;
    }

    // During active gameplay phases (PLAYING, BIDDING, DECLARE_TRUMP, KITTY_DISCARD, DEALING),
    // defer non-urgent log flushes completely so telemetry uploads never compete for
    // network bandwidth, HTTP sockets, or main thread event loop with card plays and bot turns.
    // Logs will flush authoritatively at HAND_END, GAME_OVER, table close, or page exit.
    const activePhase = this.context.phase;
    const isGameplayPhase = activePhase === 'PLAYING'
      || activePhase === 'BIDDING'
      || activePhase === 'DECLARE_TRUMP'
      || activePhase === 'KITTY_DISCARD'
      || activePhase === 'DEALING';

    if (isGameplayPhase) {
      // If queue approaches limit (>= 220), flush as emergency backpressure release
      if (this.outgoingQueue.length >= 220) {
        this.flushAsync().catch(() => {});
      }
      return;
    }

    // Non-gameplay phases (LOBBY, HAND_END, GAME_OVER): flush when queue reaches 40, or after 15s timer
    if (this.outgoingQueue.length >= 40) {
      if (this.flushTimer) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      this.flushAsync().catch(() => {});
    } else if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => {
        this.flushTimer = null;
        this.flushAsync().catch(() => {});
      }, 15000);
    }
  }

  public async flushAsync(): Promise<void> {
    if (this.isFlushing || this.outgoingQueue.length === 0) return;
    this.isFlushing = true;

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    const batch = [...this.outgoingQueue];
    this.outgoingQueue = [];

    const payload = {
      deviceId: this.deviceId,
      platform: this.platform,
      tableId: this.context.tableId || null,
      userId: this.context.userId || auth?.currentUser?.uid || null,
      seat: this.context.seat !== undefined ? this.context.seat : null,
      logs: batch
    };

    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/api/diagnostics/device-logs`;
      let token: string | undefined;
      try {
        // Fast non-blocking memory token check to avoid mutex lockups in Firebase Auth SDK
        token = await Promise.race([
          auth?.currentUser?.getIdToken(false),
          new Promise<undefined>((r) => setTimeout(() => r(undefined), 80))
        ]);
      } catch {}

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true
      });
    } catch (err) {
      // Re-queue un-sent logs if failure, capped at 100
      this.outgoingQueue = [...batch.slice(-40), ...this.outgoingQueue].slice(-100);
    } finally {
      this.isFlushing = false;
    }
  }

  private flushSync(): void {
    if (this.outgoingQueue.length === 0) return;
    const batch = [...this.outgoingQueue];
    this.outgoingQueue = [];

    const payload = {
      deviceId: this.deviceId,
      platform: this.platform,
      tableId: this.context.tableId || null,
      userId: this.context.userId || auth.currentUser?.uid || null,
      seat: this.context.seat !== undefined ? this.context.seat : null,
      logs: batch
    };

    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/api/diagnostics/device-logs`;
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      }
    } catch {}
  }
}

export const arenaLogger = new ArenaLoggerService();
